import React from 'react'
import PropTypes from 'prop-types'

import { validate } from 'email-validator'

import { isEmpty, cloneDeep } from 'lodash'
import { TextField, Checkbox, FormGroup, FormControlLabel } from '@material-ui/core'
import { Alert } from '@material-ui/lab'

import update from '@scoir/immutability'
import { NonDismissibleDialog } from '@scoir/modals'
import telephone from '@scoir/components-core/dist/components/telephone'

const { Body, Footer } = NonDismissibleDialog
const { PrimaryActionButton, CloseButton } = Footer

const GENERIC_ERROR_MESSAGE = 'Error saving user.'

const DEFAULT_USER = {
  Active: true,
  Title: '',
  FirstName: '',
  LastName: '',
  MiddleName: '',
  Email: '',
  Phone: '',
  PhoneExtension: '',
  CellPhone: '',
}

const generateState = (props) => ({
  user: props.isNew
    ? {
        ...DEFAULT_USER,
      }
    : cloneDeep(props.user),
  isPhoneValid: true,
  isCellPhoneValid: true,
  isEmailValid: true,
  inviteNow: false,
})

const onlyNumbers = (input) => input.replace(/[^0-9]/g, '')

const REQUIRED_FIELDS = ['FirstName', 'LastName', 'Email']

const validatePhoneNum = (fmtNum) => {
  if (isEmpty(fmtNum)) return true
  return fmtNum.length >= 10 && fmtNum.length <= 13
}

class ManageUserModal extends React.Component {
  constructor(props) {
    super(props)
    this.state = generateState(props)
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.open && this.props.open) {
      this.setState(generateState(this.props))
    }
  }

  canSubmit() {
    return (
      REQUIRED_FIELDS.reduce((result, field) => {
        return result && !isEmpty(this.state.user[field])
      }, true) &&
      this.state.isPhoneValid &&
      this.state.isCellPhoneValid &&
      this.state.isEmailValid
    )
  }

  handleUpdate(name, value) {
    this.setState({
      user: update(this.state.user, {
        [name]: {
          $set: value,
        },
      }),
    })
  }

  submitChanges() {
    this.setState({ errorMessage: undefined })
    this.props
      .onSubmitChanges(this.state.user, {
        inviteNow: this.state.inviteNow,
      })
      .catch((err) => {
        const promise = err.status === 409 ? err.text() : Promise.resolve(GENERIC_ERROR_MESSAGE)
        promise.then((errText) => this.setState({ errorMessage: errText }))
      })
  }

  render() {
    const confirmed = this.state.user.Confirmed

    return (
      <NonDismissibleDialog
        open={this.props.open}
        onClose={this.props.onClose}
        headerText="Staff Information"
        scroll="paper"
      >
        <Body>
          <TextField
            label="Job Title"
            name="Title"
            value={this.state.user.Title}
            helperText="eg: Director of Math Dept"
            onChange={(e) => this.handleUpdate(e.target.name, e.target.value)}
            fullWidth
            margin="normal"
            variant="outlined"
          />
          <TextField
            name="FirstName"
            value={this.state.user.FirstName}
            label="First Name"
            required
            onChange={(e) => this.handleUpdate(e.target.name, e.target.value)}
            fullWidth
            margin="normal"
            variant="outlined"
          />
          <TextField
            name="LastName"
            value={this.state.user.LastName}
            label="Last Name"
            onChange={(e) => this.handleUpdate(e.target.name, e.target.value)}
            required
            fullWidth
            margin="normal"
            variant="outlined"
          />
          <TextField
            name="MiddleName"
            value={this.state.user.MiddleName}
            label="Middle Name"
            onChange={(e) => this.handleUpdate(e.target.name, e.target.value)}
            fullWidth
            margin="normal"
            variant="outlined"
          />
          <TextField
            type="email"
            label="Email Address"
            name="Email"
            value={this.state.user.Email}
            onChange={(e) => {
              const email = e.target.value
              const isInvalid = !!email && !validate(email)
              this.setState({ isEmailValid: !isInvalid })
              this.handleUpdate(e.target.name, email)
            }}
            error={!isEmpty(this.state.user.Email) && !this.state.isEmailValid}
            required={!confirmed}
            disabled={confirmed ? true : false}
            fullWidth
            margin="normal"
            variant="outlined"
          />
          <TextField
            name="Phone"
            label="Work Phone"
            value={telephone.format(this.state.user.Phone)}
            onChange={(e) => {
              const num = e.target.value
              const fmtNum = telephone.unformat(num)
              const isPhoneValid = validatePhoneNum(fmtNum)
              this.setState({ isPhoneValid })
              this.handleUpdate('Phone', fmtNum)
            }}
            error={!this.state.isPhoneValid}
            fullWidth
            margin="normal"
            variant="outlined"
          />
          <TextField
            name="PhoneExtension"
            value={this.state.user.PhoneExtension}
            inputProps={{ maxLength: 5 }}
            label="Ext."
            onChange={(e) => {
              this.handleUpdate(e.target.name, onlyNumbers(e.target.value))
            }}
            fullWidth
            margin="normal"
            variant="outlined"
          />
          <TextField
            label="Cell Phone"
            name="CellPhone"
            value={telephone.format(this.state.user.CellPhone)}
            onChange={(e) => {
              const num = e.target.value
              const fmtNum = telephone.unformat(num)
              const isCellPhoneValid = validatePhoneNum(fmtNum)
              this.setState({ isCellPhoneValid })

              this.handleUpdate('CellPhone', fmtNum)
            }}
            error={!this.state.isCellPhoneValid}
            fullWidth
            margin="normal"
            variant="outlined"
          />
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  name="Active"
                  checked={this.state.user.Active}
                  onChange={(e) => this.handleUpdate(e.target.name, e.target.checked)}
                  color="primary"
                  disableRipple={false}
                />
              }
              label={`Is this staff an active member?`}
            />
          </FormGroup>
          {this.props.isNew && (
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    name="manage-users-invite-now"
                    checked={this.state.inviteNow}
                    onChange={(e) => this.setState({ inviteNow: e.target.checked })}
                    color="primary"
                    disableRipple={false}
                  />
                }
                label={`Send email invite immediately?`}
              />
            </FormGroup>
          )}
          {this.state.errorMessage && (
            <Alert
              severity="error"
              icon={false}
            >
              {this.state.errorMessage}
            </Alert>
          )}
        </Body>
        <Footer>
          <CloseButton>Cancel</CloseButton>
          <PrimaryActionButton
            disabled={!this.canSubmit()}
            onClick={() => this.submitChanges()}
          >
            Continue
          </PrimaryActionButton>
        </Footer>
      </NonDismissibleDialog>
    )
  }
}

ManageUserModal.propTypes = {
  open: PropTypes.bool.isRequired,
  user: PropTypes.object,
  isNew: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmitChanges: PropTypes.func.isRequired,
}

export default ManageUserModal
